import React, { useEffect, useState, useContext } from 'react';
import { alertErrorMessage, alertSuccessMessage } from '../../../customComponents/CustomAlertMessage';
import LoaderHelper from '../../../customComponents/Loading/LoaderHelper';
import AuthService from '../../../api/services/AuthService';
import { ApiConfig } from '../../../api/apiConfig/apiConfig';
import P2pLayout from './P2pLayout';
import { ProfileContext } from '../../../context/ProfileProvider';

const P2pCreatePost = () => {
    const { userDetails } = useContext(ProfileContext);
    
    // Check if KYC is completed (kycVerified === 2 means verified)
    const isKycCompleted = userDetails?.kycVerified === 2;
    const kycUpdateName = userDetails?.kycUpdateName || '';
    const [currentStep, setCurrentStep] = useState(1);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isTablet, setIsTablet] = useState(window.innerWidth < 1024);
    const [showPreview, setShowPreview] = useState(false);
    
    const [fiats, setFiats] = useState([]);
    const [cryptos, setCryptos] = useState([]);
    const [payments, setPayments] = useState([]);
    const [availablePaymentMathod, setAvailablePaymentMathod] = useState([]);
    const [paymentInputs, setPaymentInputs] = useState([]);
    const [selectedAddPaymentMethod, setSelectedAddPaymentMethod] = useState("");
    const [selectedAddPaymentMethodId, setSelectedAddPaymentMethodId] = useState("");
    const [loader, setLoader] = useState({ paymentInput: false, paymentMethods: false, pairPrice: false });
    const [paymentMethodFormData, setPaymentMethodFormData] = useState({});
    const [previewQr, setPreviewQr] = useState("");

    const [selectedBuyerPaymentMethod, setSelectedBuyerPaymentMethod] = useState([]);
    const [selectedSellerPaymentMethod, setSelectedSellerPaymentMethod] = useState([]);
    const [searchAvailPayment, setSearchAvailPayment] = useState("");

    // Market price state
    const [marketPrice, setMarketPrice] = useState(null);

    // Available balance state (for SELL ads)
    const [availableBalance, setAvailableBalance] = useState(0);

    // Field errors state
    const [fieldErrors, setFieldErrors] = useState({});

    const [formData, setFormData] = useState({
        fiat: "",
        side: "BUY",
        crypto: "",
        priceType: "FIXED",
        paymentTimeLimit: "15",
        fixedPrice: "",
        volume: "",
        min: "",
        max: "",
        remarks: "",
        agree: true,
        completedKyc: false,
        registeredUser: false,
        registeredDays: "0"
    });

    // Handle resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
            setIsTablet(window.innerWidth < 1024);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Clear field error when user types
    const handleInput = (key, value) => {
        // Restrict fixedPrice to 2 decimal places
        if (key === 'fixedPrice') {
            // Allow empty value
            if (value === '') {
                setFormData({ ...formData, [key]: value });
            } else {
                // Check if value matches pattern (numbers with max 2 decimals)
                const regex = /^\d*\.?\d{0,2}$/;
                if (regex.test(value)) {
                    setFormData({ ...formData, [key]: value });
                }
            }
        } else {
            setFormData({ ...formData, [key]: value });
        }
        // Clear the error for this field when user starts typing
        if (fieldErrors[key]) {
            setFieldErrors(prev => ({ ...prev, [key]: null }));
        }
    };

    const handlePaymentMethodAddInput = (e) => {
        const { name, value } = e.target
        setPaymentMethodFormData({ ...paymentMethodFormData, [name]: value });
    };

    const handlePaymentMethodAddImage = (event) => {
        const file = event.target.files[0];
        const { name } = event.target
        if (file) {
            const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
            const maxSize = 3 * 1024 * 1024;
            if (allowedTypes.includes(file.type) && file.size <= maxSize) {
                const imgData = URL.createObjectURL(file);
                setPreviewQr(imgData)
                setPaymentMethodFormData({ ...paymentMethodFormData, [name]: file });
            } else {
                if (!allowedTypes.includes(file.type)) {
                    alertErrorMessage("Only PNG, JPEG, and JPG file types are allowed.");
                } else {
                    alertErrorMessage("Max image size is 2MB.");
                }
                setPreviewQr("");
                setPaymentMethodFormData({ ...paymentMethodFormData, [name]: "" });
            }
        }
    };

    const toggleSellerPayment = (method) => {
        setSelectedSellerPaymentMethod(prev => {
            const exists = prev.some(item => item._id === method._id);
            if (exists) {
                return prev.filter(item => item._id !== method._id);
            } else {
                return prev.length >= 5 ? prev : [...prev, method];
            }
        });
        // Clear payment method error
        if (fieldErrors.paymentMethod) {
            setFieldErrors(prev => ({ ...prev, paymentMethod: null }));
        }
    };

    const toggleBuyerPayment = (method) => {
        setSelectedBuyerPaymentMethod(prev => {
            const exists = prev.some(item => item === method);
            if (exists) {
                return prev.filter(item => item !== method);
            } else {
                return prev.length >= 5 ? prev : [...prev, method];
            }
        });
        // Clear payment method error
        if (fieldErrors.paymentMethod) {
            setFieldErrors(prev => ({ ...prev, paymentMethod: null }));
        }
    };

    // Calculate 15% price range
    const minAllowedPrice = marketPrice ? (marketPrice * 0.85).toFixed(2) : 0;
    const maxAllowedPrice = marketPrice ? (marketPrice * 1.15).toFixed(2) : 0;

    // Validate fields and return errors object
    const validateStepFields = (step) => {
        const errors = {};
        const totalAmount = (formData.volume || 0) * (formData.fixedPrice || 0);

        if (step === 1) {
            if (!formData.fiat) {
                errors.fiat = "Please select a fiat currency";
            }
            if (!formData.side) {
                errors.side = "Please select buy or sell";
            }
            if (!formData.crypto) {
                errors.crypto = "Please select a cryptocurrency";
            }
            if (!formData.fixedPrice) {
                errors.fixedPrice = "Please enter a price";
            } else if (Number(formData.fixedPrice) <= 0) {
                errors.fixedPrice = "Price must be greater than 0";
            } else if (marketPrice && (Number(formData.fixedPrice) < minAllowedPrice || Number(formData.fixedPrice) > maxAllowedPrice)) {
                errors.fixedPrice = `Ad can be placed between ${minAllowedPrice} - ${maxAllowedPrice} ${formData.fiat}`;
            }
        }

        if (step === 2) {
            if (!formData.paymentTimeLimit) {
                errors.paymentTimeLimit = "Please select payment time limit";
            }
            
            if (!formData.volume) {
                errors.volume = "Please enter volume";
            } else if (Number(formData.volume) <= 0) {
                errors.volume = "Volume must be greater than 0";
            } else if (formData.side === "SELL" && Number(formData.volume) > availableBalance) {
                errors.volume = `Volume cannot exceed available balance (${availableBalance} ${formData.crypto})`;
            }
            
            if (!formData.min) {
                errors.min = "Please enter minimum amount";
            } else if (Number(formData.min) < 200) {
                errors.min = "Minimum amount must be at least 200";
            } else if (totalAmount > 0 && Number(formData.min) > totalAmount) {
                errors.min = `Min cannot exceed total amount (${totalAmount.toFixed(2)})`;
            }
            
            if (!formData.max) {
                errors.max = "Please enter maximum amount";
            } else if (totalAmount > 0 && Number(formData.max) > totalAmount) {
                errors.max = `Max cannot exceed total amount (${totalAmount.toFixed(2)})`;
            } else if (formData.min && Number(formData.max) < Number(formData.min)) {
                errors.max = "Max must be greater than min";
            }
            
            if (formData.side === "SELL" && selectedSellerPaymentMethod.length === 0) {
                errors.paymentMethod = "Please select at least one payment method";
            }
            if (formData.side === "BUY" && selectedBuyerPaymentMethod.length === 0) {
                errors.paymentMethod = "Please select at least one payment method";
            }
        }

        if (step === 3) {
            if (formData.registeredUser && Number(formData.registeredDays) <= 0) {
                errors.registeredDays = "Please enter valid number of days";
            }
            if (!formData.agree) {
                errors.agree = "You must accept the service agreement";
            }
        }

        return errors;
    };

    const nextStep = async () => {
        const errors = validateStepFields(currentStep);
        
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }
        
        // Fetch P2P wallet balance when moving to step 2 for SELL ads
        if (currentStep === 1 && formData.side === "SELL") {
            await fetchCryptoBalance();
        }
        
        setFieldErrors({});
        setCurrentStep(prev => Math.min(prev + 1, 3));
    };

    // Fetch crypto balance from P2P wallet for SELL ads
    const fetchCryptoBalance = async () => {
        try {
            setLoader(prev => ({ ...prev, balance: true }));
            const result = await AuthService.getUserfunds("p2p");
            if (result?.success && result?.data) {
                // Find the balance for the selected crypto
                const cryptoWallet = result.data.find(
                    wallet => wallet.short_name?.toUpperCase() === formData.crypto?.toUpperCase()
                );
                if (cryptoWallet) {
                    setAvailableBalance(Number(cryptoWallet.balance) || 0);
                } else {
                    setAvailableBalance(0);
                }
            } else {
                setAvailableBalance(0);
            }
        } catch (error) {
            setAvailableBalance(0);
        } finally {
            setLoader(prev => ({ ...prev, balance: false }));
        }
    };

    const prevStep = () => {
        setFieldErrors({});
        setCurrentStep(prev => Math.max(prev - 1, 1));
    };

    // Open confirmation modal
    const openConfirmModal = () => {
        const errors = validateStepFields(3);
        
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }
        
        // Open the modal using Bootstrap
        const modal = new window.bootstrap.Modal(document.getElementById('confirmpostModal'));
        modal.show();
    };

    // Reset form to initial state
    const resetForm = () => {
        setFormData({
            fiat: fiats?.find(f => f.short_name === "INR")?.short_name || fiats[0]?.short_name || "",
            side: "BUY",
            crypto: cryptos?.find(c => c.short_name === "USDT")?.short_name || cryptos[0]?.short_name || "",
            priceType: "FIXED",
            paymentTimeLimit: "15",
            fixedPrice: "",
            volume: "",
            min: "",
            max: "",
            remarks: "",
            agree: true,
            completedKyc: false,
            registeredUser: false,
            registeredDays: "0"
        });
        setSelectedBuyerPaymentMethod([]);
        setSelectedSellerPaymentMethod([]);
        setCurrentStep(1);
        setFieldErrors({});
        setAvailableBalance(0);
    };

    const handleSubmit = async () => {
        let payload = {
            side: formData.side,
            fiatCurrency: formData.fiat,
            qouteCurrency: formData.crypto,
            priceType: "FIXED",
            fixedPrice: formData.fixedPrice,
            paymentTimeLimit: formData.paymentTimeLimit,
            volume: formData.volume,
            minLimit: formData.min,
            maxLimit: formData.max,
            remarks: formData.remarks,
            counterpartyCondition: {
                isRegisteredCond: formData.registeredUser,
                registerDays: formData.registeredDays,
            }
        };

        if (formData.side === "SELL") {
            const methodIds = selectedSellerPaymentMethod.map(item => item._id);
            const methodNames = [...new Set(selectedSellerPaymentMethod.map(item => item.name))];
            payload = { ...payload, paymentMethodIds: methodIds, paymentMethodType: methodNames };
        }

        if (formData.side === "BUY") {
            payload = { ...payload, paymentMethodType: selectedBuyerPaymentMethod };
        }

        LoaderHelper.loaderStatus(true);
        try {
            const result = await AuthService.createAd(payload);
            if (result?.success) {
                LoaderHelper.loaderStatus(false);
                alertSuccessMessage("P2P Ad Created Successfully!");
                // Close modal and reset form
                const modal = window.bootstrap.Modal.getInstance(document.getElementById('confirmpostModal'));
                if (modal) modal.hide();
                resetForm();
            } else {
                LoaderHelper.loaderStatus(false);
                alertErrorMessage(result?.message);
            }
        } catch (error) {
            LoaderHelper.loaderStatus(false);
            alertErrorMessage("Something went wrong!");
        }
    };

    const getFiatCurrency = async () => {
        try {
            const result = await AuthService.getFiatCurrency();
            const result2 = await AuthService.getCurrency();
            const result3 = await AuthService.getUserPaymentMethods();
            
            if (result?.success) {
                const fiatList = result?.data || [];
                setFiats(fiatList);
                
                // Set default fiat: INR if exists, otherwise first item
                const hasINR = fiatList.some(f => f.short_name === "INR");
                const defaultFiat = hasINR ? "INR" : (fiatList[0]?.short_name || "");
                setFormData(prev => ({ ...prev, fiat: defaultFiat }));
            }
            
            if (result2?.success) {
                const cryptoList = result2?.data || [];
                setCryptos(cryptoList);
                
                // Set default crypto: USDT if exists, otherwise first item
                const hasUSDT = cryptoList.some(c => c.short_name === "USDT");
                const defaultCrypto = hasUSDT ? "USDT" : (cryptoList[0]?.short_name || "");
                setFormData(prev => ({ ...prev, crypto: defaultCrypto }));
            }
            
            if (result3?.success) setPayments(result3?.data || [])
        } catch (error) {
            LoaderHelper.loaderStatus(false);
        } finally { LoaderHelper.loaderStatus(false); }
    };

    // Fetch market price for the selected pair
    const getPairPrice = async (crypto, fiat) => {
        if (!crypto || !fiat) return;
        try {
            setLoader(prev => ({ ...prev, pairPrice: true }));
            const result = await AuthService.getPairPrice(crypto, fiat);
            if (result?.success && result?.data?.price) {
                const price = Number(result.data.price);
                setMarketPrice(price);
                // Set initial fixed price to current market price
                setFormData(prev => ({ ...prev, fixedPrice: price.toFixed(2) }));
            } else {
                setMarketPrice(null);
            }
        } catch (error) {
            setMarketPrice(null);
        } finally {
            setLoader(prev => ({ ...prev, pairPrice: false }));
        }
    };

    const getAvailPaymentMethod = async () => {
        try {
            setLoader({ paymentMethods: true })
            const result = await AuthService.getAllPaymentMethods();
            if (result?.success) setAvailablePaymentMathod(result?.data || [])
        } catch (error) {
        } finally { setLoader({ paymentMethods: false }) }
    };

    const getPaymentMethodFields = async (id, name) => {
        // Check KYC before allowing to add payment method
        if (!isKycCompleted) {
            alertErrorMessage("Please complete KYC verification before adding a payment method.");
            return;
        }

        try {
            setLoader({ paymentInput: true })
            setSelectedAddPaymentMethod("")
            setSelectedAddPaymentMethodId("")
            setPaymentInputs([])
            setPaymentMethodFormData({})
            setPreviewQr("")
            const result = await AuthService.getPaymentMethodFields(id);
            if (result?.success) {
                setPaymentInputs(result?.data || []);
                setSelectedAddPaymentMethodId(id)
                setSelectedAddPaymentMethod(name);
                if (result?.data?.length > 0) {
                    const defaultForm = {};
                    result?.data?.forEach((item) => {
                        // Pre-fill name fields with kycUpdateName if KYC is completed
                        const isNameField = false
                        defaultForm[item.field] = isNameField && isKycCompleted && kycUpdateName ? kycUpdateName : "";
                    });
                    setPaymentMethodFormData(defaultForm);
                }
            }
        } catch (error) {
            alertErrorMessage("Something went wrong!");
        } finally { setLoader({ paymentInput: false }) }
    };

    const handleSubmitPaymentMethod = () => {
        // Check KYC before submitting
        if (!isKycCompleted) {
            alertErrorMessage("Please complete KYC verification before adding a payment method.");
            return;
        }

        if (!paymentInputs || paymentInputs?.length === 0) {
            alertErrorMessage("No payment fields available.");
            return;
        }
        for (let item of paymentInputs) {
            // Skip file/files type fields (QR code is optional)
            if (item.type !== "files" && item.type !== "file") {
                if (!paymentMethodFormData[item.field] || paymentMethodFormData[item.field].toString().trim() === "") {
                    alertErrorMessage(`Please enter ${formatLabel(item.label || item.field)}`);
                    return;
                }
            }
        };
        submitPaymentMethod(paymentMethodFormData);
    };

    const submitPaymentMethod = async (paymentMethodFormData) => {
        try {
            LoaderHelper.loaderStatus(true);
            const formDataObj = new FormData();
            Object.entries(paymentMethodFormData).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    formDataObj.append(key, value);
                }
            });
            if (selectedAddPaymentMethodId) {
                formDataObj.append("templateId", selectedAddPaymentMethodId);
            }
            const result = await AuthService.addUserPaymentMethod(formDataObj);
            if (result?.success) {
                const result2 = await AuthService.getUserPaymentMethods();
                if (result2?.success) setPayments(result2?.data || []);
                setSelectedAddPaymentMethod("")
                setSelectedAddPaymentMethodId("")
                setPaymentInputs([])
                setPaymentMethodFormData({})
                setPreviewQr("")
                alertSuccessMessage("Payment method added successfully!");
            } else {
                alertErrorMessage(result?.message || "Something went wrong!");
            }
        } catch (error) {
            alertErrorMessage("Something went wrong!");
        } finally {
            LoaderHelper.loaderStatus(false);
        }
    }

    const formatLabel = (text) => {
        return text.replace(/([A-Z])/g, " $1").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    };

    useEffect(() => {
        getFiatCurrency();
        getAvailPaymentMethod();
    }, []);

    // Fetch pair price when crypto or fiat changes
    useEffect(() => {
        if (formData.crypto && formData.fiat) {
            getPairPrice(formData.crypto, formData.fiat);
        }
    }, [formData.crypto, formData.fiat]);

    const totalAmount = (formData.volume || 0) * (formData.fixedPrice || 0);

    const getPaymentMethods = () => {
        if (formData.side === "BUY") return selectedBuyerPaymentMethod;
        return selectedSellerPaymentMethod.map(m => m.name || m.type);
    };

    // Helper to get input style with error state
    const getInputStyle = (fieldName, baseStyle) => ({
        ...baseStyle,
        border: fieldErrors[fieldName] ? '1px solid #ef4444' : baseStyle.border || '1px solid #2a2a3a',
        boxShadow: fieldErrors[fieldName] ? '0 0 0 3px rgba(239, 68, 68, 0.1)' : 'none'
    });

    // Error message component
    const FieldError = ({ fieldName }) => {
        if (!fieldErrors[fieldName]) return null;
        return (
            <div style={{
                color: '#ef4444',
                fontSize: isMobile ? '11px' : '12px',
                marginTop: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
            }}>
                <span>⚠</span> {fieldErrors[fieldName]}
            </div>
        );
    };

    // Responsive Styles
    const styles = {
        container: {
            display: 'grid',
            gridTemplateColumns: isTablet ? '1fr' : '1fr 380px',
            gap: isMobile ? '16px' : '24px',
            maxWidth: '100%'
        },
        formCard: {
            background: '#12121a',
            borderRadius: isMobile ? '12px' : '16px',
            border: '1px solid #1e1e2d',
            padding: isMobile ? '16px' : isTablet ? '24px' : '32px'
        },
        header: {
            marginBottom: isMobile ? '20px' : '32px'
        },
        title: {
            fontSize: isMobile ? '20px' : '24px',
            fontWeight: '600',
            color: '#fff',
            margin: '0 0 8px 0'
        },
        subtitle: {
            color: '#666',
            fontSize: isMobile ? '12px' : '14px',
            margin: 0
        },
        stepper: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: isMobile ? '20px' : '32px',
            gap: '0'
        },
        stepCircle: (active, completed) => ({
            width: isMobile ? '28px' : '32px',
            height: isMobile ? '28px' : '32px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: isMobile ? '12px' : '14px',
            fontWeight: '600',
            background: completed ? '#22c55e' : active ? '#2563eb' : 'transparent',
            border: `2px solid ${completed ? '#22c55e' : active ? '#2563eb' : '#2a2a3a'}`,
            color: completed || active ? '#fff' : '#666',
            transition: 'all 0.3s ease',
            flexShrink: 0
        }),
        stepLine: (completed) => ({
            width: isMobile ? '40px' : isTablet ? '80px' : '120px',
            height: '2px',
            background: completed ? '#22c55e' : '#2a2a3a',
            transition: 'all 0.3s ease'
        }),
        sectionTitle: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: isMobile ? '14px' : '16px',
            fontWeight: '600',
            color: '#fff',
            marginBottom: isMobile ? '16px' : '20px'
        },
        sectionIcon: {
            color: '#2563eb'
        },
        inputGroup: {
            marginBottom: isMobile ? '16px' : '20px'
        },
        label: {
            display: 'block',
            color: '#888',
            fontSize: isMobile ? '12px' : '13px',
            marginBottom: '8px'
        },
        select: {
            width: '100%',
            padding: isMobile ? '12px 14px' : '14px 16px',
            background: '#0d0d14',
            border: '1px solid #2a2a3a',
            borderRadius: '10px',
            color: '#fff',
            fontSize: isMobile ? '13px' : '14px',
            cursor: 'pointer',
            appearance: 'none',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%23888' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 16px center',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
        },
        input: {
            width: '100%',
            padding: isMobile ? '12px 14px' : '14px 16px',
            background: '#0d0d14',
            border: '1px solid #2a2a3a',
            borderRadius: '10px',
            color: '#fff',
            fontSize: isMobile ? '13px' : '14px',
            outline: 'none',
            boxSizing: 'border-box',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
        },
        inputWithSuffix: {
            display: 'flex',
            alignItems: 'center',
            background: '#0d0d14',
            border: '1px solid #2a2a3a',
            borderRadius: '10px',
            overflow: 'hidden',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
        },
        inputInner: {
            flex: 1,
            padding: isMobile ? '12px 14px' : '14px 16px',
            background: 'transparent',
            border: 'none',
            color: '#fff',
            fontSize: isMobile ? '13px' : '14px',
            outline: 'none',
            minWidth: 0
        },
        inputSuffix: {
            padding: isMobile ? '12px 10px' : '14px 16px',
            color: '#888',
            fontSize: isMobile ? '12px' : '14px',
            borderLeft: '1px solid #2a2a3a',
            whiteSpace: 'nowrap'
        },
        availableText: {
            color: '#22c55e',
            fontSize: isMobile ? '10px' : '12px',
            marginLeft: '8px',
            display: isMobile ? 'none' : 'inline'
        },
        feeBox: {
            background: 'linear-gradient(90deg, rgba(34, 197, 94, 0.1) 0%, transparent 100%)',
            border: '1px solid rgba(34, 197, 94, 0.2)',
            borderRadius: '10px',
            padding: isMobile ? '10px 14px' : '12px 16px',
            color: '#22c55e',
            fontSize: isMobile ? '13px' : '14px',
            marginTop: isMobile ? '16px' : '20px'
        },
        infoBox: {
            background: '#0d0d14',
            borderRadius: '10px',
            padding: isMobile ? '12px' : '16px',
            marginTop: '16px'
        },
        pairText: {
            color: '#f59e0b',
            fontSize: isMobile ? '14px' : '15px',
            fontWeight: '600',
            marginBottom: '8px'
        },
        infoText: {
            color: '#a8b4c4',
            fontSize: isMobile ? '12px' : '13px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '6px',
            marginBottom: '4px'
        },
        paymentMethod: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: isMobile ? '12px 14px' : '14px 16px',
            background: '#0d0d14',
            border: '1px solid #2a2a3a',
            borderRadius: '10px',
            marginBottom: '12px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
        },
        paymentMethodActive: {
            borderColor: '#2563eb',
            background: 'rgba(37, 99, 235, 0.1)'
        },
        paymentMethodError: {
            borderColor: '#ef4444',
            boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.1)'
        },
        checkbox: {
            width: isMobile ? '18px' : '20px',
            height: isMobile ? '18px' : '20px',
            accentColor: '#2563eb',
            flexShrink: 0
        },
        addMethodBtn: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#2563eb',
            background: 'transparent',
            border: 'none',
            fontSize: isMobile ? '13px' : '14px',
            cursor: 'pointer',
            padding: '8px 0'
        },
        textarea: {
            width: '100%',
            padding: isMobile ? '12px 14px' : '14px 16px',
            background: '#0d0d14',
            border: '1px solid #2a2a3a',
            borderRadius: '10px',
            color: '#fff',
            fontSize: isMobile ? '13px' : '14px',
            outline: 'none',
            minHeight: isMobile ? '80px' : '100px',
            resize: 'vertical',
            boxSizing: 'border-box',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
        },
        checkboxLabel: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            color: '#ccc',
            fontSize: isMobile ? '13px' : '14px',
            cursor: 'pointer',
            marginBottom: '16px'
        },
        buttonGroup: {
            display: 'flex',
            gap: '12px',
            marginTop: isMobile ? '24px' : '32px',
            flexDirection: isMobile ? 'column-reverse' : 'row'
        },
        btnPrimary: {
            flex: isMobile ? 'unset' : 1,
            padding: isMobile ? '14px 20px' : '14px 24px',
            background: '#22c55e',
            border: 'none',
            borderRadius: '10px',
            color: '#fff',
            fontSize: isMobile ? '14px' : '15px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            width: isMobile ? '100%' : 'auto'
        },
        btnSecondary: {
            padding: isMobile ? '14px 20px' : '14px 24px',
            background: 'transparent',
            border: '1px solid #2a2a3a',
            borderRadius: '10px',
            color: '#fff',
            fontSize: isMobile ? '14px' : '15px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            width: isMobile ? '100%' : 'auto'
        },
        previewCard: {
            background: '#12121a',
            borderRadius: isMobile ? '12px' : '16px',
            border: '1px solid #1e1e2d',
            overflow: 'hidden',
            position: isTablet ? 'relative' : 'sticky',
            top: isTablet ? '0' : '20px'
        },
        previewHeader: {
            padding: isMobile ? '16px' : '20px 24px',
            borderBottom: '1px solid #1e1e2d',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        previewTitle: {
            fontSize: isMobile ? '14px' : '16px',
            color: '#888',
            margin: 0
        },
        previewHighlight: {
            color: formData.side === 'BUY' ? '#22c55e' : '#ef4444',
            fontWeight: '600'
        },
        previewBody: {
            padding: isMobile ? '16px' : '24px'
        },
        previewRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: isMobile ? '12px' : '16px',
            flexWrap: 'wrap',
            gap: '8px'
        },
        previewLabel: {
            color: '#666',
            fontSize: isMobile ? '13px' : '14px'
        },
        previewValue: {
            color: '#fff',
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: '500',
            textAlign: 'right'
        },
        previewBadges: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            justifyContent: 'flex-end'
        },
        previewBadge: {
            padding: isMobile ? '4px 10px' : '6px 12px',
            background: 'rgba(37, 99, 235, 0.1)',
            border: '1px solid rgba(37, 99, 235, 0.3)',
            borderRadius: '6px',
            color: '#a8b4c4',
            fontSize: isMobile ? '11px' : '12px'
        },
        previewRemarks: {
            marginTop: isMobile ? '16px' : '20px',
            paddingTop: isMobile ? '16px' : '20px',
            borderTop: '1px solid #1e1e2d'
        },
        previewRemarksTitle: {
            color: '#888',
            fontSize: isMobile ? '12px' : '13px',
            marginBottom: '8px'
        },
        previewRemarksText: {
            color: '#ccc',
            fontSize: isMobile ? '12px' : '13px',
            lineHeight: '1.5'
        },
        agreementBox: {
            background: fieldErrors.agree ? 'rgba(239, 68, 68, 0.05)' : '#0d0d14',
            borderRadius: '10px',
            padding: isMobile ? '12px' : '16px',
            marginBottom: '20px',
            border: fieldErrors.agree ? '1px solid #ef4444' : '1px solid transparent',
            transition: 'all 0.2s ease'
        },
        helperText: {
            color: '#666',
            fontSize: isMobile ? '11px' : '12px',
            marginTop: '6px'
        },
        mobilePreviewToggle: {
            display: isTablet ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            background: '#0d0d14',
            borderRadius: '10px',
            marginBottom: '16px',
            cursor: 'pointer',
            border: '1px solid #2a2a3a'
        },
        gridTwoCol: {
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? '12px' : '16px'
        },
        paymentGrid: {
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? '10px' : '16px'
        }
    };

    // Preview Card Component
    const PreviewCard = () => {
        const totalValue = (Number(formData.volume) || 0) * (Number(formData.fixedPrice) || 0);
        const paymentTimeLabels = { '15': '15 Min', '30': '30 Min', '45': '45 Min', '60': '1 Hour', '120': '2 Hours' };
        
        return (
            <div style={styles.previewCard}>
                {/* Header */}
                <div style={{
                    padding: isMobile ? '16px' : '20px 24px',
                    background: formData.side === 'BUY' ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(34, 197, 94, 0.05) 100%)' : 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(239, 68, 68, 0.05) 100%)',
                    borderBottom: '1px solid #1e1e2d'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <span style={{ color: '#888', fontSize: '12px', display: 'block', marginBottom: '4px' }}>Ad Preview</span>
                            <h3 style={{ 
                                color: '#fff', 
                                fontSize: isMobile ? '18px' : '20px', 
                                fontWeight: '700', 
                                margin: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <span style={{ 
                                    color: formData.side === 'BUY' ? '#22c55e' : '#ef4444',
                                    background: formData.side === 'BUY' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                    padding: '4px 10px',
                                    borderRadius: '6px',
                                    fontSize: '14px'
                                }}>
                                    {formData.side}
                                </span>
                                {formData.crypto || 'USDT'}
                            </h3>
                        </div>
                        {isTablet && (
                            <button className="closebtn" 
                                onClick={() => setShowPreview(false)}
                            >
                                ×
                            </button>
                        )}
                    </div>
                </div>

                {/* Price Highlight */}
                <div style={{
                    padding: isMobile ? '16px' : '20px 24px',
                    borderBottom: '1px solid #1e1e2d',
                    textAlign: 'center'
                }}>
                    <span style={{ color: '#666', fontSize: '12px', display: 'block', marginBottom: '4px' }}>Price per {formData.crypto || 'USDT'}</span>
                    <div style={{ 
                        color: formData.side === 'BUY' ? '#22c55e' : '#ef4444', 
                        fontSize: isMobile ? '28px' : '32px', 
                        fontWeight: '700',
                        letterSpacing: '-1px'
                    }}>
                        {formData.fixedPrice || '0.00'} <span style={{ fontSize: '16px', color: '#888' }}>{formData.fiat}</span>
                    </div>
                    {marketPrice && (
                        <span style={{ color: '#666', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                            Market: {marketPrice.toFixed(2)} {formData.fiat}
                        </span>
                    )}
                </div>

                {/* Details Grid */}
                <div style={{ padding: isMobile ? '16px' : '20px 24px' }}>
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr 1fr', 
                        gap: '16px',
                        marginBottom: '16px'
                    }}>
                        <div style={{ background: '#0d0d14', padding: '12px', borderRadius: '10px' }}>
                            <span style={{ color: '#666', fontSize: '11px', display: 'block', marginBottom: '4px' }}>Amount</span>
                            <span style={{ color: '#fff', fontSize: '15px', fontWeight: '600' }}>
                                {formData.volume || '0'} <span style={{ color: '#888', fontSize: '12px' }}>{formData.crypto}</span>
                            </span>
                        </div>
                        <div style={{ background: '#0d0d14', padding: '12px', borderRadius: '10px' }}>
                            <span style={{ color: '#666', fontSize: '11px', display: 'block', marginBottom: '4px' }}>Total Value</span>
                            <span style={{ color: '#fff', fontSize: '15px', fontWeight: '600' }}>
                                {totalValue.toFixed(2)} <span style={{ color: '#888', fontSize: '12px' }}>{formData.fiat}</span>
                            </span>
                        </div>
                        <div style={{ background: '#0d0d14', padding: '12px', borderRadius: '10px' }}>
                            <span style={{ color: '#666', fontSize: '11px', display: 'block', marginBottom: '4px' }}>Min Limit</span>
                            <span style={{ color: '#fff', fontSize: '15px', fontWeight: '600' }}>
                                {formData.min || '0'} <span style={{ color: '#888', fontSize: '12px' }}>{formData.fiat}</span>
                            </span>
                        </div>
                        <div style={{ background: '#0d0d14', padding: '12px', borderRadius: '10px' }}>
                            <span style={{ color: '#666', fontSize: '11px', display: 'block', marginBottom: '4px' }}>Max Limit</span>
                            <span style={{ color: '#fff', fontSize: '15px', fontWeight: '600' }}>
                                {formData.max || '0'} <span style={{ color: '#888', fontSize: '12px' }}>{formData.fiat}</span>
                            </span>
                        </div>
                    </div>

                    {/* Payment Time */}
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '12px',
                        background: '#0d0d14',
                        borderRadius: '10px',
                        marginBottom: '16px'
                    }}>
                        <span style={{ color: '#666', fontSize: '13px' }}>⏱ Payment Time Limit</span>
                        <span style={{ color: '#fff', fontSize: '14px', fontWeight: '500' }}>
                            {paymentTimeLabels[formData.paymentTimeLimit] || formData.paymentTimeLimit + ' Min'}
                        </span>
                    </div>

                    {/* Payment Methods */}
                    <div style={{ marginBottom: '16px' }}>
                        <span style={{ color: '#666', fontSize: '12px', display: 'block', marginBottom: '8px' }}>Payment Methods</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {getPaymentMethods().length > 0 ? (
                                getPaymentMethods().map((method, i) => (
                                    <span key={i} style={{
                                        padding: '6px 12px',
                                        background: 'rgba(37, 99, 235, 0.1)',
                                        border: '1px solid rgba(37, 99, 235, 0.3)',
                                        borderRadius: '6px',
                                        // color: '#60a5fa',
                                        fontSize: '12px',
                                        fontWeight: '500'
                                    }}>{method}</span>
                                ))
                            ) : (
                                <span style={{ 
                                    color: '#666', 
                                    fontSize: '12px',
                                    padding: '6px 12px',
                                    background: '#0d0d14',
                                    borderRadius: '6px',
                                    border: '1px dashed #2a2a3a'
                                }}>
                                    No payment method selected
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Counterparty Conditions */}
                    {(formData.completedKyc || formData.registeredUser) && (
                        <div style={{ marginBottom: '16px' }}>
                            <span style={{ color: '#666', fontSize: '12px', display: 'block', marginBottom: '8px' }}>Counterparty Conditions</span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {formData.completedKyc && (
                                    <span style={{
                                        padding: '6px 12px',
                                        background: 'rgba(34, 197, 94, 0.1)',
                                        border: '1px solid rgba(34, 197, 94, 0.3)',
                                        borderRadius: '6px',
                                        color: '#4ade80',
                                        fontSize: '12px'
                                    }}>✓ KYC Verified</span>
                                )}
                                {formData.registeredUser && (
                                    <span style={{
                                        padding: '6px 12px',
                                        background: 'rgba(245, 158, 11, 0.1)',
                                        border: '1px solid rgba(245, 158, 11, 0.3)',
                                        borderRadius: '6px',
                                        color: '#fbbf24',
                                        fontSize: '12px'
                                    }}>Registered {formData.registeredDays}+ days</span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Remarks */}
                    <div style={{
                        padding: '12px',
                        background: '#0d0d14',
                        borderRadius: '10px',
                        borderLeft: '3px solid #2563eb'
                    }}>
                        <span style={{ color: '#666', fontSize: '11px', display: 'block', marginBottom: '6px' }}>📝 Remarks</span>
                        <span style={{ color: '#aaa', fontSize: '13px', lineHeight: '1.5' }}>
                            {formData.remarks || 'No remarks added'}
                        </span>
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    padding: isMobile ? '16px' : '16px 24px',
                    borderTop: '1px solid #1e1e2d',
                    background: '#0a0a0f'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ color: '#666', fontSize: '11px' }}>Fee: 0%</span>
                        <span style={{ color: '#888', fontSize: '11px' }}>
                            {formData.side === 'SELL' ? `Available: ${availableBalance} ${formData.crypto}` : 'Preview Only'}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <P2pLayout title="Create Post">
            <div className="p2p-dashboard-container">
                {/* Mobile Preview Toggle */}
                {isTablet && !showPreview && (
                    <div style={styles.mobilePreviewToggle} onClick={() => setShowPreview(true)}>
                        <span style={{ color: '#888', fontSize: '14px' }}>
                            Preview: <span style={styles.previewHighlight}>{formData.side} {formData.crypto} AD</span>
                        </span>
                        <span style={{ color: '#2563eb', fontSize: '14px' }}>View →</span>
                    </div>
                )}

                {/* Mobile Preview Overlay */}
                {isTablet && showPreview && (
                    <div style={{ marginBottom: '16px' }}>
                        <PreviewCard />
                    </div>
                )}

                <div style={styles.container}>
                    {/* Form Section */}
                    <div style={styles.formCard}>
                        <div style={styles.header}>
                            <h2 style={styles.title}>Create New Post</h2>
                            <p style={styles.subtitle}>Lorem Ipsum is simply dummy text of the printing and typesetting industry</p>
                        </div>

                        {/* Stepper */}
                        <div style={styles.stepper}>
                            <div style={styles.stepCircle(currentStep === 1, currentStep > 1)}>
                                {currentStep > 1 ? '✓' : '1'}
                            </div>
                            <div style={styles.stepLine(currentStep > 1)}></div>
                            <div style={styles.stepCircle(currentStep === 2, currentStep > 2)}>
                                {currentStep > 2 ? '✓' : '2'}
                            </div>
                            <div style={styles.stepLine(currentStep > 2)}></div>
                            <div style={styles.stepCircle(currentStep === 3, false)}>3</div>
                        </div>

                        {/* Step 1: I want to use + Price Settings */}
                        {currentStep === 1 && (
                            <>
                                <div style={styles.sectionTitle}>
                                    <span style={styles.sectionIcon}>▶</span>
                                    I want to use
                                </div>
                                <div style={styles.gridTwoCol}>
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>Fiat</label>
                                        <select 
                                            style={getInputStyle('fiat', styles.select)} 
                                            value={formData.fiat} 
                                            onChange={(e) => handleInput("fiat", e.target.value)}
                                        >
                                            <option value="" hidden>Select</option>
                                            {fiats?.map((f, i) => <option key={i} value={f.short_name}>{f.short_name}</option>)}
                                        </select>
                                        <FieldError fieldName="fiat" />
                                    </div>
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>Side</label>
                                        <select 
                                            style={getInputStyle('side', styles.select)} 
                                            value={formData.side} 
                                            onChange={(e) => handleInput("side", e.target.value)}
                                        >
                                            <option value="BUY">BUY</option>
                                            <option value="SELL">SELL</option>
                                        </select>
                                        <FieldError fieldName="side" />
                                    </div>
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>Crypto</label>
                                        <select 
                                            style={getInputStyle('crypto', styles.select)} 
                                            value={formData.crypto} 
                                            onChange={(e) => handleInput("crypto", e.target.value)}
                                        >
                                            <option value="" hidden>Select</option>
                                            {cryptos.map((c, i) => <option key={i} value={c.short_name}>{c.short_name}</option>)}
                                        </select>
                                        <FieldError fieldName="crypto" />
                                    </div>
                                </div>

                                <div style={styles.feeBox}>Fee: 0%</div>

                                <div style={{ marginTop: isMobile ? '24px' : '32px' }}>
                                    <div style={styles.sectionTitle}>
                                        <span style={styles.sectionIcon}>▶</span>
                                        Price Settings
                                    </div>
                                    <div style={styles.gridTwoCol}>
                                        <div style={styles.inputGroup}>
                                            <label style={styles.label}>Price Type</label>
                                            <select 
                                                style={styles.select} 
                                                value={formData.priceType} 
                                                onChange={(e) => handleInput("priceType", e.target.value)}
                                            >
                                                <option value="FIXED">Fixed</option>
                                            </select>
                                        </div>
                                        <div style={styles.inputGroup}>
                                            <label style={styles.label}>Fixed Price</label>
                                            <div style={getInputStyle('fixedPrice', styles.inputWithSuffix)}>
                                                <input
                                                    type="number"
                                                    style={styles.inputInner}
                                                    value={formData.fixedPrice}
                                                    onChange={(e) => handleInput("fixedPrice", e.target.value)}
                                                    placeholder="e.g. 85.50"
                                                    onWheel={(e) => e.target.blur()}
                                                />
                                                <span style={styles.inputSuffix}>{formData.fiat}</span>
                                            </div>
                                            <FieldError fieldName="fixedPrice" />
                                        </div>
                                    </div>
                                </div>

                                {/* Market Price Info Box */}
                                <div style={styles.infoBox}>
                                    {loader.pairPrice ? (
                                        <div style={{ textAlign: 'center', padding: '10px' }}>
                                            <span style={{ color: '#666' }}>Loading market price...</span>
                                        </div>
                                    ) : marketPrice ? (
                                        <>
                                            <div style={{ color: '#9ca3af', fontSize: isMobile ? '13px' : '14px', marginBottom: '10px' }}>
                                                {formData.crypto}/{formData.fiat}
                                            </div>
                                            <div style={{ color: '#6b7280', fontSize: isMobile ? '12px' : '13px', marginBottom: '6px' }}>
                                                <span style={{ marginRight: '6px' }}>•</span> 
                                                Current Price: <span style={{ }}>{marketPrice.toFixed(2)}</span> {formData.fiat}
                                            </div>
                                            <div style={{ color: '#6b7280', fontSize: isMobile ? '12px' : '13px' }}>
                                                <span style={{ marginRight: '6px' }}>•</span> 
                                                Ad can be placed between: <span style={{  }}>{minAllowedPrice}</span> - <span style={{  }}>{maxAllowedPrice}</span> {formData.fiat}
                                            </div>
                                        </>
                                    ) : (
                                        <div style={{ color: '#d97706', fontSize: '13px' }}>
                                            ⚠ Unable to fetch market price. Please try again.
                                        </div>
                                    )}
                                </div>

                                <div style={styles.buttonGroup}>
                                    <button style={styles.btnPrimary} onClick={nextStep}>
                                        Continue
                                    </button>
                                </div>
                            </>
                        )}

                        {/* Step 2: Transaction Settings + Payment Method */}
                        {currentStep === 2 && (
                            <>
                                <div style={styles.sectionTitle}>
                                    <span style={styles.sectionIcon}>▶</span>
                                    Transaction Settings
                                </div>

                                <div style={styles.gridTwoCol}>
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>Payment Time Limit</label>
                                        <select
                                            style={getInputStyle('paymentTimeLimit', styles.select)}
                                            value={formData.paymentTimeLimit}
                                            onChange={(e) => handleInput("paymentTimeLimit", e.target.value)}
                                        >
                                            <option value="15">15 Minutes</option>
                                            <option value="30">30 Minutes</option>
                                            <option value="45">45 Minutes</option>
                                            <option value="60">1 Hour</option>
                                            <option value="120">2 Hours</option>
                                        </select>
                                        <FieldError fieldName="paymentTimeLimit" />
                                    </div>
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>Volume</label>
                                        <div style={getInputStyle('volume', styles.inputWithSuffix)}>
                                            <input
                                                type="number"
                                                style={styles.inputInner}
                                                value={formData.volume}
                                                onChange={(e) => handleInput("volume", e.target.value)}
                                                placeholder="Enter Volume"
                                                onWheel={(e) => e.target.blur()}
                                            />
                                            <span style={styles.inputSuffix}>{formData.crypto}</span>
                                            {formData.side === "SELL" && (
                                                <span style={styles.availableText}>Available</span>
                                            )}
                                        </div>
                                        {fieldErrors.volume ? (
                                            <FieldError fieldName="volume" />
                                        ) : formData.side === "SELL" ? (
                                            <div style={styles.helperText}>
                                                {loader.balance ? 'Loading balance...' : `Available: ${availableBalance} ${formData.crypto}`}
                                            </div>
                                        ) : null}
                                    </div>
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>Min</label>
                                        <div style={getInputStyle('min', styles.inputWithSuffix)}>
                                            <input
                                                type="number"
                                                style={styles.inputInner}
                                                value={formData.min}
                                                onChange={(e) => handleInput("min", e.target.value)}
                                                placeholder="e.g. 500"
                                                onWheel={(e) => e.target.blur()}
                                            />
                                            <span style={styles.inputSuffix}>{formData.fiat}</span>
                                        </div>
                                        {fieldErrors.min ? (
                                            <FieldError fieldName="min" />
                                        ) : (
                                            <div style={styles.helperText}>at least 200 {formData.fiat}</div>
                                        )}
                                    </div>
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>Max</label>
                                        <div style={getInputStyle('max', styles.inputWithSuffix)}>
                                            <input
                                                type="number"
                                                style={styles.inputInner}
                                                value={formData.max}
                                                onChange={(e) => handleInput("max", e.target.value)}
                                                placeholder="e.g. 10000"
                                                onWheel={(e) => e.target.blur()}
                                            />
                                            <span style={styles.inputSuffix}>{formData.fiat}</span>
                                        </div>
                                        {fieldErrors.max && (
                                            <FieldError fieldName="max" />
                                        ) }
                                    </div>
                                </div>

                                <div style={{ marginTop: isMobile ? '24px' : '32px' }}>
                                    <div style={styles.sectionTitle}>
                                        <span style={styles.sectionIcon}>▶</span>
                                        Select Payment Method
                                        {fieldErrors.paymentMethod && (
                                            <span style={{ color: '#ef4444', fontSize: '12px', fontWeight: '400', marginLeft: '8px' }}>
                                                (Required)
                                            </span>
                                        )}
                                    </div>

                                    {/* For SELL: Show user's own payment methods with full details */}
                                    {formData.side === "SELL" && (
                                        <>
                                            {payments?.length > 0 ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                    {payments.map((method) => {
                                                        const isSelected = selectedSellerPaymentMethod.some(m => m._id === method._id);
                                                        // Keys to exclude from display
                                                        const excludeKeys = ['_id', 'templateId', 'type', 'name', 'qrCode'];
                                                        // Get displayable fields
                                                        const displayFields = Object.entries(method).filter(
                                                            ([key, value]) => !excludeKeys.includes(key) && value && value !== ''
                                                        );
                                                        
                                                        return (
                                                            <div
                                                                key={method._id}
                                                                style={{
                                                                    background: '#0d0d14',
                                                                    border: isSelected ? '1px solid #2563eb' : (fieldErrors.paymentMethod ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid #2a2a3a'),
                                                                    borderRadius: '10px',
                                                                    padding: isMobile ? '12px' : '16px',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s ease',
                                                                    ...(isSelected ? { background: 'rgba(37, 99, 235, 0.1)' } : {})
                                                                }}
                                                                onClick={() => toggleSellerPayment(method)}
                                                            >
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        <span style={{ color: '#2563eb', fontSize: isMobile ? '14px' : '15px', fontWeight: '600' }}>
                                                                            {method.name || method.type}
                                                                        </span>
                                                                        {method.type && method.type !== method.name && (
                                                                            <span style={{ 
                                                                                color: '#666', 
                                                                                fontSize: '11px',
                                                                                background: '#1a1a2e',
                                                                                padding: '2px 8px',
                                                                                borderRadius: '4px'
                                                                            }}>
                                                                                {method.type}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        onChange={() => { }}
                                                                        style={styles.checkbox}
                                                                    />
                                                                </div>
                                                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '8px' }}>
                                                                    {displayFields.map(([key, value]) => (
                                                                        <div key={key} style={{ fontSize: '12px' }}>
                                                                            <span style={{ color: '#666' }}>{formatLabel(key)}: </span>
                                                                            <span style={{ color: '#aaa' }}>{value}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                {method.qrCode && (
                                                                    <div style={{ 
                                                                        marginTop: '12px', 
                                                                        paddingTop: '12px', 
                                                                        borderTop: '1px solid #2a2a3a',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '12px'
                                                                    }}>
                                                                        <img 
                                                                            src={`${ApiConfig.baseImage}${method.qrCode}`}
                                                                            alt="QR Code"
                                                                            style={{
                                                                                width: isMobile ? '60px' : '80px',
                                                                                height: isMobile ? '60px' : '80px',
                                                                                objectFit: 'contain',
                                                                                borderRadius: '8px',
                                                                                background: '#fff',
                                                                                padding: '4px'
                                                                            }}
                                                                            onError={(e) => {
                                                                                e.target.style.display = 'none';
                                                                            }}
                                                                        />
                                                                        <span style={{ color: '#666', fontSize: '11px' }}>QR Code</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div style={{ 
                                                    color: '#888', 
                                                    fontSize: '13px', 
                                                    padding: '20px', 
                                                    textAlign: 'center',
                                                    background: '#0d0d14',
                                                    borderRadius: '10px',
                                                    border: fieldErrors.paymentMethod ? '1px solid #ef4444' : '1px solid #2a2a3a'
                                                }}>
                                                    No payment methods added. Please add a payment method first.
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {/* For BUY: Show available payment method types */}
                                    {formData.side === "BUY" && (
                                        <div style={styles.paymentGrid}>
                                            {availablePaymentMathod.slice(0, 6).map((method) => {
                                                const isSelected = selectedBuyerPaymentMethod.includes(method.name);
                                                return (
                                                    <div
                                                        key={method._id}
                                                        style={{
                                                            ...styles.paymentMethod,
                                                            ...(isSelected ? styles.paymentMethodActive : {}),
                                                            ...(fieldErrors.paymentMethod && !isSelected ? { borderColor: 'rgba(239, 68, 68, 0.5)' } : {})
                                                        }}
                                                        onClick={() => toggleBuyerPayment(method.name)}
                                                    >
                                                        <span style={{ color: '#fff', fontSize: isMobile ? '13px' : '14px' }}>{method.name}</span>
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => { }}
                                                            style={styles.checkbox}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {fieldErrors.paymentMethod && (
                                        <div style={{
                                            color: '#ef4444',
                                            fontSize: isMobile ? '11px' : '12px',
                                            marginTop: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            <span>⚠</span> {fieldErrors.paymentMethod}
                                        </div>
                                    )}

                                    <button
                                        style={styles.addMethodBtn}
                                        data-bs-toggle="modal"
                                        data-bs-target={formData.side === "SELL" ? "#sellModal" : "#buyPaymentModal"}
                                    >
                                        <span style={{ fontSize: '18px' }}>+</span> Add New Method
                                    </button>
                                </div>

                                <div style={styles.buttonGroup}>
                                    <button style={styles.btnSecondary} onClick={prevStep}>Back</button>
                                    <button style={styles.btnPrimary} onClick={nextStep}>Continue</button>
                                </div>
                            </>
                        )}

                        {/* Step 3: Remarks + Counterparty Conditions */}
                        {currentStep === 3 && (
                            <>
                                <div style={styles.sectionTitle}>
                                    <span style={styles.sectionIcon}>▶</span>
                                    Remarks (Optional)
                                </div>
                                <textarea
                                    style={styles.textarea}
                                    value={formData.remarks}
                                    onChange={(e) => handleInput("remarks", e.target.value)}
                                    placeholder="Enter remarks..."
                                />

                                <div style={{ marginTop: isMobile ? '24px' : '32px' }}>
                                    <div style={styles.sectionTitle}>
                                        <span style={styles.sectionIcon}>▶</span>
                                        Counterparty Conditions
                                    </div>

                                    {/* <label style={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={formData.completedKyc}
                                            onChange={() => handleInput("completedKyc", !formData.completedKyc)}
                                            style={styles.checkbox}
                                        />
                                        <span>Completed KYC</span>
                                    </label> */}

                                    <label style={{ ...styles.checkboxLabel, flexWrap: 'wrap' }}>
                                        <input
                                            type="checkbox"
                                            checked={formData.registeredUser}
                                            onChange={() => handleInput("registeredUser", !formData.registeredUser)}
                                            style={styles.checkbox}
                                        />
                                        <span>Registered</span>
                                        {formData.registeredUser && (
                                            <div style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '8px', 
                                                marginLeft: isMobile ? '0' : '8px',
                                                marginTop: isMobile ? '8px' : '0',
                                                width: isMobile ? '100%' : 'auto'
                                            }}>
                                                <input
                                                    type="number"
                                                    style={getInputStyle('registeredDays', { ...styles.input, width: '80px', padding: '8px 12px' })}
                                                    value={formData.registeredDays}
                                                    onChange={(e) => handleInput("registeredDays", e.target.value)}
                                                    min="0"
                                                />
                                                <span style={{ color: '#888' }}>day(s) ago</span>
                                            </div>
                                        )}
                                    </label>
                                    {fieldErrors.registeredDays && (
                                        <div style={{
                                            color: '#ef4444',
                                            fontSize: isMobile ? '11px' : '12px',
                                            marginTop: '-8px',
                                            marginBottom: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            <span>⚠</span> {fieldErrors.registeredDays}
                                        </div>
                                    )}
                                </div>

                                <div style={styles.agreementBox}>
                                    <label style={{ ...styles.checkboxLabel, marginBottom: 0 }}>
                                        <input
                                            type="checkbox"
                                            checked={formData.agree}
                                            onChange={() => handleInput("agree", !formData.agree)}
                                            style={styles.checkbox}
                                        />
                                        <span style={{ fontSize: isMobile ? '12px' : '14px' }}>
                                            I Have Read And Agree To Peer-To-Peer (P2P) Service Agreement
                                        </span>
                                    </label>
                                </div>
                                {fieldErrors.agree && (
                                    <div style={{
                                        color: '#ef4444',
                                        fontSize: isMobile ? '11px' : '12px',
                                        marginTop: '-12px',
                                        marginBottom: '16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}>
                                        <span>⚠</span> {fieldErrors.agree}
                                    </div>
                                )}

                                <div style={styles.buttonGroup}>
                                    <button style={styles.btnSecondary} onClick={prevStep}>Cancel</button>
                                    <button style={{ ...styles.btnPrimary, background: formData.side === 'SELL' ? '#ef4444' : '#22c55e' }} onClick={openConfirmModal}>
                                        Create Ad
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Desktop Preview Card */}
                    {!isTablet && <PreviewCard />}
                </div>
            </div>

            {/* Seller payment modal - Add New Payment Method */}
            <div className="modal fade" id="sellModal" tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content" style={{ background: '#12121a', border: '1px solid #2a2a3a', borderRadius: '16px' }}>
                        <div className="modal-header" style={{ borderBottom: '1px solid #2a2a3a', padding: '20px 24px' }}>
                            <h5 style={{ color: '#fff', fontSize: '18px', fontWeight: '600', margin: 0 }}>
                                {selectedAddPaymentMethod ? 'Set My Payment Method' : 'Add Payment Method'}
                            </h5>
                            <button 
                                type="button" 
                                className="btn-close btn-close-white" 
                                data-bs-dismiss="modal"
                                onClick={() => {
                                    setSelectedAddPaymentMethod("");
                                    setSelectedAddPaymentMethodId("");
                                    setPaymentInputs([]);
                                    setPaymentMethodFormData({});
                                    setPreviewQr("");
                                }}
                            ></button>
                        </div>
                        <div className="modal-body" style={{ padding: '24px', maxHeight: '70vh', overflowY: 'auto' }}>
                            {/* Tips */}
                            <div style={{ 
                                background: 'rgba(245, 158, 11, 0.1)', 
                                border: '1px solid rgba(245, 158, 11, 0.3)',
                                borderRadius: '10px',
                                padding: '12px 16px',
                                marginBottom: '20px',
                                display: 'flex',
                                gap: '10px',
                                alignItems: 'flex-start'
                            }}>
                                <span style={{ color: '#f59e0b' }}>ℹ</span>
                                <span style={{ color: '#d4a574', fontSize: '13px', lineHeight: '1.5' }}>
                                    Tips: The added payment method will be shown to the buyer during the transaction to accept fiat transfers. 
                                    Please ensure that the information is correct, real and matches your KYC information.
                                </span>
                            </div>

                            {/* Step 1: Select Payment Method Type */}
                            {!selectedAddPaymentMethod && (
                                <>
                                    <div style={{ marginBottom: '16px' }}>
                                        <label style={{ color: '#888', fontSize: '13px', marginBottom: '8px', display: 'block' }}>
                                            Select Payment Method Type
                                        </label>
                                        {loader.paymentMethods ? (
                                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                                <div className="spinner-border text-primary" role="status" />
                                            </div>
                                        ) : (
                                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
                                                {availablePaymentMathod?.map((method) => (
                                                    <div
                                                        key={method._id}
                                                        style={{
                                                            padding: '14px 16px',
                                                            background: '#0d0d14',
                                                            border: '1px solid #2a2a3a',
                                                            borderRadius: '10px',
                                                            cursor: isKycCompleted ? 'pointer' : 'not-allowed',
                                                            transition: 'all 0.2s ease',
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            opacity: !isKycCompleted ? 0.6 : 1
                                                        }}
                                                        onClick={() => {
                                                            if (isKycCompleted) {
                                                                getPaymentMethodFields(method._id, method.name);
                                                            } else {
                                                                alertErrorMessage("Please complete KYC verification before adding a payment method.");
                                                            }
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            if (isKycCompleted) {
                                                                e.currentTarget.style.borderColor = '#2563eb';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => e.currentTarget.style.borderColor = '#2a2a3a'}
                                                    >
                                                        <span style={{ color: '#fff', fontSize: '14px' }}>{method.name}</span>
                                                        {(method.name === 'IMPS' || method.name === 'UPI') && (
                                                            <span style={{ 
                                                                background: 'rgba(34, 197, 94, 0.1)', 
                                                                color: '#22c55e', 
                                                                fontSize: '10px', 
                                                                padding: '2px 8px', 
                                                                borderRadius: '4px' 
                                                            }}>
                                                                Recommended
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {/* Step 2: Fill Payment Method Details */}
                            {selectedAddPaymentMethod && (
                                <>
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '10px', 
                                        marginBottom: '20px',
                                        paddingBottom: '16px',
                                        borderBottom: '1px solid #2a2a3a'
                                    }}>
                                        <button
                                            style={{ 
                                                background: 'transparent', 
                                                border: 'none', 
                                                color: '#888', 
                                                cursor: 'pointer',
                                                padding: '4px'
                                            }}
                                            onClick={() => {
                                                setSelectedAddPaymentMethod("");
                                                setSelectedAddPaymentMethodId("");
                                                setPaymentInputs([]);
                                                setPaymentMethodFormData({});
                                                setPreviewQr("");
                                            }}
                                        >
                                            ← Back
                                        </button>
                                        <span style={{ color: '#2563eb', fontWeight: '600' }}>{selectedAddPaymentMethod}</span>
                                    </div>

                                    {!isKycCompleted && (
                                        <div style={{ 
                                            background: 'rgba(245, 158, 11, 0.1)', 
                                            border: '1px solid rgba(245, 158, 11, 0.3)',
                                            borderRadius: '10px',
                                            padding: '12px 16px',
                                            marginBottom: '20px',
                                            color: '#d4a574',
                                            fontSize: '13px'
                                        }}>
                                            <strong>KYC Verification Required:</strong> Please complete your KYC verification before adding a payment method.
                                        </div>
                                    )}

                                    {kycUpdateName && (
                                        <div style={{ marginBottom: '16px' }}>
                                            <label style={{ color: '#888', fontSize: '13px', marginBottom: '8px', display: 'block' }}>
                                                Name
                                            </label>
                                            <input
                                                type="text"
                                                value={kycUpdateName}
                                                disabled
                                                style={{
                                                    width: '100%',
                                                    padding: '12px 16px',
                                                    background: '#1a1a2e',
                                                    border: '1px solid #2a2a3a',
                                                    borderRadius: '10px',
                                                    color: '#fff',
                                                    fontSize: '14px',
                                                    cursor: 'not-allowed',
                                                    opacity: 0.6
                                                }}
                                            />
                                            <small style={{ color: '#666', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                                                This name is taken from your KYC verification
                                            </small>
                                        </div>
                                    )}

                                    {loader.paymentInput ? (
                                        <div style={{ textAlign: 'center', padding: '40px' }}>
                                            <div className="spinner-border text-primary" role="status" />
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                            {paymentInputs?.map((field, index) => {
                                                // Check if this is a name field
                                                const isNameField = false
                                                const currentValue = paymentMethodFormData[field.field] || '';
                                                const placeholder = isNameField && isKycCompleted && kycUpdateName && !currentValue 
                                                                    ? kycUpdateName 
                                                                    : (field.placeholder || `${(field.label || field.field)}`);

                                                return (
                                                    <div key={index}>
                                                        <label style={{ color: '#888', fontSize: '13px', marginBottom: '8px', display: 'block' }}>
                                                            {(field.label || field.field)}
                                                            {(field.type === 'file' || field.type === 'files') ? (
                                                                <span style={{ color: '#666' }}> (Optional)</span>
                                                            ) : (
                                                                field.required !== false && <span style={{ color: '#ef4444' }}> *</span>
                                                            )}
                                                        </label>
                                                        {field.type === 'file' || field.type === 'files' ? (
                                                            <div>
                                                                <div style={{
                                                                    border: '2px dashed #2a2a3a',
                                                                    borderRadius: '10px',
                                                                    padding: '20px',
                                                                    textAlign: 'center',
                                                                    cursor: isKycCompleted ? 'pointer' : 'not-allowed',
                                                                    position: 'relative',
                                                                    opacity: !isKycCompleted ? 0.6 : 1
                                                                }}>
                                                                    <input
                                                                        type="file"
                                                                        name={field.field}
                                                                        accept=".jpg,.jpeg,.png,.bmp"
                                                                        onChange={handlePaymentMethodAddImage}
                                                                        disabled={!isKycCompleted}
                                                                        style={{
                                                                            position: 'absolute',
                                                                            top: 0,
                                                                            left: 0,
                                                                            width: '100%',
                                                                            height: '100%',
                                                                            opacity: 0,
                                                                            cursor: isKycCompleted ? 'pointer' : 'not-allowed'
                                                                        }}
                                                                    />
                                                                    {previewQr ? (
                                                                        <img src={previewQr} alt="Preview" style={{ maxWidth: '100px', maxHeight: '100px', borderRadius: '8px' }} />
                                                                    ) : (
                                                                        <>
                                                                            <div style={{ color: '#666', fontSize: '24px', marginBottom: '8px' }}>⬆</div>
                                                                            <div style={{ color: '#888', fontSize: '13px' }}>Upload QR Code</div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                                <small style={{ color: '#666', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                                                                    JPG/JPEG/PNG, less than 3MB
                                                                </small>
                                                            </div>
                                                        ) : (
                                                            <input
                                                                type={field.type || 'text'}
                                                                name={field.field}
                                                                value={currentValue}
                                                                onChange={handlePaymentMethodAddInput}
                                                                placeholder={placeholder}
                                                                disabled={!isKycCompleted}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '12px 16px',
                                                                    background: '#0d0d14',
                                                                    border: '1px solid #2a2a3a',
                                                                    borderRadius: '10px',
                                                                    color: '#fff',
                                                                    fontSize: '14px',
                                                                    outline: 'none',
                                                                    opacity: !isKycCompleted ? 0.6 : 1,
                                                                    cursor: !isKycCompleted ? 'not-allowed' : 'text'
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                );
                                            })}

                                            {/* Warning */}
                                            <div style={{ 
                                                background: 'rgba(239, 68, 68, 0.1)', 
                                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                                borderRadius: '10px',
                                                padding: '12px 16px',
                                                marginTop: '8px'
                                            }}>
                                                <span style={{ color: '#f87171', fontSize: '12px', lineHeight: '1.5' }}>
                                                    Warning: Please ensure all information is accurate and matches your KYC details.
                                                </span>
                                            </div>

                                            {/* Buttons */}
                                            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                                                <button
                                                    style={{
                                                        flex: 1,
                                                        padding: '12px 20px',
                                                        background: 'transparent',
                                                        border: '1px solid #2a2a3a',
                                                        borderRadius: '10px',
                                                        color: '#fff',
                                                        fontSize: '14px',
                                                        cursor: 'pointer'
                                                    }}
                                                    data-bs-dismiss="modal"
                                                    onClick={() => {
                                                        setSelectedAddPaymentMethod("");
                                                        setSelectedAddPaymentMethodId("");
                                                        setPaymentInputs([]);
                                                        setPaymentMethodFormData({});
                                                        setPreviewQr("");
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    style={{
                                                        flex: 1,
                                                        padding: '12px 20px',
                                                        background: '#2563eb',
                                                        border: 'none',
                                                        borderRadius: '10px',
                                                        color: '#fff',
                                                        fontSize: '14px',
                                                        fontWeight: '600',
                                                        cursor: isKycCompleted ? 'pointer' : 'not-allowed',
                                                        opacity: !isKycCompleted ? 0.6 : 1
                                                    }}
                                                    onClick={handleSubmitPaymentMethod}
                                                    disabled={!isKycCompleted}
                                                >
                                                    Confirm
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Buyer payment modal */}
            <div className="modal fade" id="buyPaymentModal" tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content p2p-modal-content">
                        <div className="modal-header p2p-modal-header">
                            <h5 className="p2p-modal-title">Select Payment Method</h5>
                            <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal"></button>
                        </div>
                        <div className="modal-body p2p-modal-body">
                            <p style={{ color: '#888', marginBottom: '20px' }}>Select up to 5 methods</p>
                            <div style={{ marginBottom: '16px' }}>
                                <input
                                    type="search"
                                    placeholder="Search payment method..."
                                    value={searchAvailPayment}
                                    onChange={(e) => setSearchAvailPayment(e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                            <div className="p2p-modal-options">
                                {loader?.paymentMethods ? (
                                    <div style={{ textAlign: 'center', padding: '20px' }}>
                                        <div className="spinner-border text-primary" role="status" />
                                    </div>
                                ) : availablePaymentMathod?.length > 0 && availablePaymentMathod
                                    .filter(method => !searchAvailPayment || method?.name?.toLowerCase().includes(searchAvailPayment.toLowerCase()))
                                    .map(method => (
                                        <label
                                            key={method?._id}
                                            className={`p2p-payment-option ${selectedBuyerPaymentMethod?.includes(method?.name) ? 'p2p-selected' : ''}`}
                                            onClick={() => toggleBuyerPayment(method?.name)}
                                        >
                                            <span>{method?.name}</span>
                                            <input
                                                type="checkbox"
                                                checked={selectedBuyerPaymentMethod?.includes(method?.name)}
                                                onChange={() => { }}
                                            />
                                        </label>
                                    ))
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm Post Modal */}
            <div className="modal fade payment_method_pop userprofile_pop" id="confirmpostModal" tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content bg-dark text-white rounded-4">
                        <div className="modal-header border-0">
                            <h5 className="modal-title">Confirm to Post</h5>
                            <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal"></button>
                        </div>
                        <div className="modal-body text-center detailuser_profile_two">
                            <ul className="list_upi">
                                <li>Type<span className={formData.side === 'SELL' ? 'text-danger' : 'text-success'}>{formData.side}</span></li>
                                <li>Asset<span>{formData.crypto}</span></li>
                                <li>Currency<span>{formData.fiat}</span></li>
                                <li>Price Type<span>Fixed</span></li>
                                <li>Fixed<span className="text-success">{formData.fixedPrice} {formData.fiat}</span></li>
                                <li>Order Limit<span>{formData.min} {formData.fiat} - {formData.max} {formData.fiat}</span></li>
                                <li>Total Trading Amount<span>{formData.volume} {formData.crypto}</span></li>
                                <li>Reserved Fee<span>0.00 {formData.crypto}</span></li>
                                <li><hr /></li>
                                <li>
                                    Payment Method
                                    <span>
                                        {getPaymentMethods().length > 0 ? (
                                            getPaymentMethods().map((method, i) => (
                                                <abbr key={i}>{method}</abbr>
                                            ))
                                        ) : (
                                            'None'
                                        )}
                                    </span>
                                </li>
                                <li>Payment Time Limit<span>{formData.paymentTimeLimit === '60' ? '1 Hour' : formData.paymentTimeLimit === '120' ? '2 Hours' : `${formData.paymentTimeLimit} min`}</span></li>
                                <li>Available Region(s)<span>All Regions</span></li>
                                <li>Status<span className="text-success">● Online</span></li>
                                {formData.remarks && (
                                    <li>Remarks<span>{formData.remarks}</span></li>
                                )}
                            </ul>
                            <div className="d-flex submit_button">
                                <button className="btn bgnone" data-bs-dismiss="modal">Cancel</button>
                                <button className="btn" onClick={handleSubmit}>Confirm Post</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </P2pLayout>
    );
};

export default P2pCreatePost;
